import { Button, TextField } from '@execonline-inc/execonline-ui';
import { useQueryClient } from '@tanstack/react-query';
import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NotTranslated, T } from '../../../../Translations';
import { serverErrorMessage, submitMetadata } from '../../Api';
import { ConnectionResource, SubmitFailure } from '../../Types';

interface Props {
  resource: ConnectionResource;
  onDone: () => void;
}

type State =
  | { kind: 'editing'; metadataUrl: string; error: SubmitFailure }
  | { kind: 'saving'; metadataUrl: string };

// Re-runs the verify path with a new metadata URL. The connection returns to
// the attribute diagnostic gate afterwards, so a re-test confirms the swap.
const UpdateMetadataUrl: React.FC<Props> = ({ resource, onDone }) => {
  const [state, setState] = React.useState<State>({
    kind: 'editing',
    metadataUrl: resource.payload.metadataUrl.getOrElseValue(''),
    error: nothing(),
  });
  const queryClient = useQueryClient();

  const save = () => {
    if (state.kind !== 'editing') {
      return;
    }
    const { metadataUrl } = state;
    setState({ kind: 'saving', metadataUrl });
    submitMetadata(resource.links, metadataUrl.trim()).fork(
      (err) => setState({ kind: 'editing', metadataUrl, error: just(serverErrorMessage(err)) }),
      () => {
        queryClient.invalidateQueries({ queryKey: ['sso-configuration'] });
        queryClient.invalidateQueries({ queryKey: ['sso-configurations'] });
        onDone();
      },
    );
  };

  return (
    <div>
      <TextField
        label={<T kind="Metadata URL" />}
        value={state.metadataUrl}
        onChange={(event) =>
          state.kind === 'editing' && setState({ ...state, metadataUrl: event.target.value })
        }
        helperText={
          <T kind="Updating the metadata URL re-verifies the connection; you will be asked to run a test sign-in again." />
        }
        inputClassName="border-none"
        fullWidth
        data-intercom-target="sso-metadata-url-input-field"
      />
      {state.kind === 'editing' &&
        state.error
          .map((message) => (
            <p key="error" className="mt-2 text-sm text-destructive">
              {message
                .map((text) => <NotTranslated key="server" text={text} />)
                .getOrElse(() => (
                  <T kind="We are unable to process your request at this time" />
                ))}
            </p>
          ))
          .getOrElse(() => <></>)}
      <div className="mt-3 flex gap-3">
        <Button color="primary" variant="outlined" size="sm" onClick={onDone}>
          <T kind="Cancel" />
        </Button>
        <Button
          color="success"
          size="sm"
          isLoading={state.kind === 'saving'}
          disabled={state.metadataUrl.trim().length === 0 || state.kind === 'saving'}
          onClick={save}
          data-intercom-target="sso-save-metadata-url-button"
        >
          <T kind="Verify connection" />
        </Button>
      </div>
    </div>
  );
};

export default observer(UpdateMetadataUrl);
